import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
}

interface PromptOptions extends ConfirmOptions {
  placeholder?: string;
  defaultValue?: string;
}

interface ConfirmDialogContextValue {
  /** Show a confirmation dialog and return a promise that resolves to true/false */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** Show a prompt dialog and return a promise that resolves to the input value or null */
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | undefined>(undefined);

interface DialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  variant: 'danger' | 'warning' | 'info';
  showInput: boolean;
  inputPlaceholder: string;
  inputValue: string;
  resolve: ((value: boolean | string | null) => void) | null;
  isPrompt: boolean;
}

const initialState: DialogState = {
  isOpen: false,
  title: '',
  message: '',
  confirmLabel: 'Confirmar',
  cancelLabel: 'Cancelar',
  variant: 'info',
  showInput: false,
  inputPlaceholder: '',
  inputValue: '',
  resolve: null,
  isPrompt: false,
};

interface ConfirmDialogProviderProps {
  children: ReactNode;
}

export function ConfirmDialogProvider({ children }: ConfirmDialogProviderProps) {
  const [state, setState] = useState<DialogState>(initialState);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel ?? 'Confirmar',
        cancelLabel: options.cancelLabel ?? 'Cancelar',
        variant: options.variant ?? 'info',
        showInput: false,
        inputPlaceholder: '',
        inputValue: '',
        resolve: resolve as (value: boolean | string | null) => void,
        isPrompt: false,
      });
    });
  }, []);

  const prompt = useCallback((options: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel ?? 'Aceptar',
        cancelLabel: options.cancelLabel ?? 'Cancelar',
        variant: options.variant ?? 'info',
        showInput: true,
        inputPlaceholder: options.placeholder ?? '',
        inputValue: options.defaultValue ?? '',
        resolve: resolve as (value: boolean | string | null) => void,
        isPrompt: true,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (state.resolve) {
      if (state.isPrompt) {
        state.resolve(state.inputValue);
      } else {
        state.resolve(true);
      }
    }
    setState(initialState);
  }, [state]);

  const handleCancel = useCallback(() => {
    if (state.resolve) {
      if (state.isPrompt) {
        state.resolve(null);
      } else {
        state.resolve(false);
      }
    }
    setState(initialState);
  }, [state]);

  const handleInputChange = useCallback((value: string) => {
    setState((prev) => ({ ...prev, inputValue: value }));
  }, []);

  return (
    <ConfirmDialogContext.Provider value={{ confirm, prompt }}>
      {children}
      <ConfirmDialog
        isOpen={state.isOpen}
        title={state.title}
        message={state.message}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        variant={state.variant}
        showInput={state.showInput}
        inputPlaceholder={state.inputPlaceholder}
        inputValue={state.inputValue}
        onInputChange={handleInputChange}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog(): ConfirmDialogContextValue {
  const context = useContext(ConfirmDialogContext);
  if (context === undefined) {
    throw new Error('useConfirmDialog must be used within a ConfirmDialogProvider');
  }
  return context;
}
